import { Controller } from "@hotwired/stimulus"
import DocumentStore from "../builder/document_store"
import BuilderApi from "../builder/api"

const AUTOSAVE_DELAY = 800

// Orchestrates the builder: owns the store, autosave, undo and the device
// switch. Sibling controllers reach the store through `element.builder`
// rather than through Stimulus values, so the document is never serialised
// between panes.
export default class extends Controller {
  static targets = ["status", "undo", "redo", "device", "part", "conflict", "conflictMessage"]
  static values = {
    document: Object,
    headerDocument: Object,
    footerDocument: Object,
    lockVersion: Number,
    schema: Object,
    canvasUrl: String,
    documentUrl: String,
    nodesUrl: String,
    sectionsUrl: String,
    cloneUrl: String,
    assetsUrl: String,
    themeUrl: String,
    theme: Object,
    fragmentUrl: String
  }

  connect() {
    this.part = "page"
    this.documents = {
      page: this.documentValue,
      header: this.headerDocumentValue,
      footer: this.footerDocumentValue
    }
    this.store = new DocumentStore(this.documentValue, this.lockVersionValue)
    this.schema = this.schemaValue
    this.urls = {
      canvas: this.canvasUrlValue,
      document: this.documentUrlValue,
      nodes: this.nodesUrlValue,
      sections: this.sectionsUrlValue,
      clone: this.cloneUrlValue,
      assets: this.assetsUrlValue,
      theme: this.themeUrlValue,
      fragment: this.fragmentUrlValue
    }

    // Published for the pane controllers, which connect after this one.
    this.element.builder = this
    this.unsubscribe = this.store.subscribe((event) => this.onStoreEvent(event))

    this.setDeviceByName("desktop")
    this.refreshHistoryButtons()
    this.onKeydown = (event) => this.handleKeydown(event)
    document.addEventListener("keydown", this.onKeydown)
    this.element.dispatchEvent(new CustomEvent("builder:ready", { bubbles: true }))
  }

  disconnect() {
    this.unsubscribe?.()
    document.removeEventListener("keydown", this.onKeydown)
    clearTimeout(this.saveTimer)
  }

  // --- store plumbing -----------------------------------------------------

  onStoreEvent(event) {
    if (event.type === "select" || event.type === "breakpoint") return

    this.refreshHistoryButtons()
    this.markUnsaved()
    this.scheduleSave()
  }

  refreshHistoryButtons() {
    if (this.hasUndoTarget) this.undoTarget.disabled = !this.store.canUndo
    if (this.hasRedoTarget) this.redoTarget.disabled = !this.store.canRedo
  }

  // --- inserting ----------------------------------------------------------

  // Node ids come from the server, so insertion is the one edit that must
  // round-trip before it can be applied.
  async createNode(type, parentId, index) {
    const { ok, payload } = await BuilderApi.createNode(this.urls.nodes, type)
    if (!ok) return null

    payload.node.__html = payload.html
    return this.store.insert(payload.node, parentId, index) ? payload.node : null
  }

  // --- duplicating and pasting --------------------------------------------

  // The server mints the ids and vets what is copied, so both paths go through
  // it rather than cloning in the browser.
  async duplicate(id = this.store.selectedId) {
    const node = this.store.node(id)
    const parent = this.store.parentOf(id)
    if (!node || !parent) return

    const { ok, payload } = await BuilderApi.cloneNode(this.urls.clone, node)
    if (!ok) return

    payload.node.__html = payload.html
    this.store.insert(payload.node, parent.id, this.store.indexOf(id) + 1)
  }

  copy(id = this.store.selectedId) {
    const node = this.store.node(id)
    if (node) this.clipboard = JSON.parse(JSON.stringify(node))
  }

  // Pastes as a sibling of the selection, falling back to the page. Refuses
  // outright when neither can hold the type: letting an illegal tree into the
  // document would only surface later as a save the editor cannot complete.
  async paste() {
    if (!this.clipboard) return

    const selected = this.store.selectedId
    const parent = selected ? this.store.parentOf(selected) : this.store.root
    const type = this.clipboard.type

    const asSibling = this.accepts(parent, type)
    const target = asSibling ? parent : this.store.root
    if (!this.accepts(target, type)) return

    const { ok, payload } = await BuilderApi.cloneNode(this.urls.clone, this.clipboard)
    if (!ok) return

    const index = asSibling && selected ? this.store.indexOf(selected) + 1 : (target.children || []).length
    payload.node.__html = payload.html
    this.store.insert(payload.node, target.id, index)
  }

  accepts(parent, type) {
    return Boolean(parent) && (this.schema.children[parent.type] || []).includes(type)
  }

  // --- saving -------------------------------------------------------------

  markUnsaved() {
    this.setStatus(this.t("unsaved"))
  }

  scheduleSave() {
    clearTimeout(this.saveTimer)
    this.saveTimer = setTimeout(() => this.save(), AUTOSAVE_DELAY)
  }

  async save() {
    this.setStatus(this.t("saving"))
    const { ok, status, payload } = await BuilderApi.saveDocument(
      this.urls.document, this.store.document, this.store.lockVersion, this.part
    )

    if (ok) {
      this.store.lockVersion = payload.lock_version
      this.setStatus(this.t("saved"))
      return
    }

    if (status === 409) {
      this.showConflict(payload.error)
      return
    }

    this.setStatus(this.t("save_failed"))
    if (payload.errors?.length) this.showConflict(payload.errors[0])
  }

  setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  showConflict(message) {
    if (!this.hasConflictTarget) return
    this.conflictMessageTarget.textContent = message
    this.conflictTarget.hidden = false
  }

  reload() { window.location.reload() }

  // --- history ------------------------------------------------------------

  undo(event) { event?.preventDefault(); this.store.undo() }
  redo(event) { event?.preventDefault(); this.store.redo() }

  handleKeydown(event) {
    if (event.target.isContentEditable) return

    if (event.key === "Escape") return this.store.select(null)

    const mod = event.metaKey || event.ctrlKey
    if (!mod) return

    const key = event.key.toLowerCase()
    const shortcut = { z: () => (event.shiftKey ? this.store.redo() : this.store.undo()),
                       d: () => this.duplicate(),
                       c: () => this.copy(),
                       v: () => this.paste() }[key]
    if (!shortcut) return

    event.preventDefault()
    shortcut()
  }

  // --- page / header / footer ---------------------------------------------

  // Header and footer are shared by every page, so switching parts saves the
  // one being left before swapping the document under the editor.
  async setPart(event) {
    const wanted = event.currentTarget.dataset.part
    if (wanted === this.part) return

    clearTimeout(this.saveTimer)
    await this.save()

    this.documents[this.part] = this.store.document
    this.part = wanted
    this.store.replaceDocument(this.documents[wanted])

    this.partTargets.forEach((button) => {
      const active = button.dataset.part === wanted
      button.classList.toggle("bg-white", active)
      button.classList.toggle("shadow-sm", active)
      button.classList.toggle("dark:bg-slate-700", active)
    })

    this.element.dispatchEvent(new CustomEvent("builder:part", { bubbles: true, detail: { part: wanted } }))
  }

  // --- device -------------------------------------------------------------

  setDevice(event) {
    this.setDeviceByName(event.currentTarget.dataset.device)
  }

  setDeviceByName(device) {
    const widths = { desktop: "100%", tablet: "768px", mobile: "390px" }
    const breakpoints = { desktop: "lg", tablet: "md", mobile: "base" }

    this.device = device
    this.deviceTargets.forEach((button) => {
      const active = button.dataset.device === device
      button.classList.toggle("bg-white", active)
      button.classList.toggle("shadow-sm", active)
      button.classList.toggle("dark:bg-slate-700", active)
    })

    this.element.dispatchEvent(
      new CustomEvent("builder:device", { bubbles: true, detail: { width: widths[device] } })
    )
    this.store.setBreakpoint(breakpoints[device])
  }

  // All copy comes from the schema payload, which the server fills from
  // config/locales. No user-facing string is hardcoded here.
  t(key) {
    return this.schema.labels.ui?.[key] || key
  }
}
